import { useEffect, useRef, useState } from "react";
import { eventPackages, eventTypes } from "@/mocks/bbCoffeeData";

export default function Event() {
  const sectionRef = useRef<HTMLElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<(typeof eventPackages)[0] | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const openModal = (pkg: (typeof eventPackages)[0]) => {
    setSelectedPkg(pkg);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPkg(null);
  };

  const bookViaWhatsApp = (eventType: string) => {
    if (!selectedPkg) return;
    const drinksList = selectedPkg.drinks.join(", ");
    const message = `Hi BB COFFEE! I'm interested in booking ${selectedPkg.name} for my ${eventType}.\n\nPackage Details:\n- Drinks: ${drinksList}\n- Price: RM${selectedPkg.price} per cup\n${selectedPkg.bonus ? `- Bonus: ${selectedPkg.bonus}` : ""}\n\nPlease provide more details. Thank you!`;
    const waUrl = `https://wa.me/60133316383?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank");
    closeModal();
  };

  return (
    <section id="event" ref={sectionRef} className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          alt="BB COFFEE catering event experience"
          className="w-full h-full object-cover object-center"
          src="https://readdy.ai/api/search-image?query=Elegant%20coffee%20catering%20setup%20at%20outdoor%20event%20with%20string%20lights%2C%20warm%20golden%20evening%20atmosphere%2C%20people%20enjoying%20drinks%2C%20bokeh%20lights%2C%20cream%20and%20gold%20tones%2C%20festive%20sophisticated%20ambiance%2C%20editorial%20event%20photography%2C%20soft%20dreamy%20mood&width=1920&height=800&seq=bb-event-bg&orientation=landscape"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/90 via-background-950/80 to-background-950/90" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 reveal-on-scroll">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-primary-300 bg-primary-500/10 rounded-full border border-primary-400/20">
            Coffee Catering
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
            Bring the Caf&eacute; to Your <span className="text-primary-400">Event Experience</span>
          </h2>
          <p className="text-background-200 leading-relaxed text-sm md:text-base max-w-xl mx-auto">
            Whether it&apos;s a wedding, private event, birthday party, or project collaboration BB Coffee brings the full coffee truck experience to you.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {eventPackages.map((pkg) => (
            <div
              key={pkg.id}
              role="button"
              tabIndex={0}
              onClick={() => openModal(pkg)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openModal(pkg); }}
              className={`reveal-on-scroll relative rounded-2xl p-5 transition-all duration-500 hover:-translate-y-2 cursor-pointer border ${
                pkg.popular
                  ? "bg-primary-500/20 border-primary-400/30 hover:bg-primary-500/30"
                  : "bg-background-50/10 border-background-50/10 hover:bg-background-50/20"
              }`}
            >
              {pkg.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-primary-500 text-background-50 rounded-full">
                  Most Popular
                </span>
              )}
              <div className="text-center mb-4">
                <h3 className="font-heading font-semibold text-background-50 text-lg mb-1">{pkg.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl font-bold text-primary-400">RM{pkg.price}</span>
                  <span className="text-xs text-background-300">per cup</span>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-background-300">Includes</p>
                {pkg.drinks.map((drink) => (
                  <div key={drink} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-xs text-primary-400" />
                    </div>
                    <span className="text-sm text-background-200">{drink}</span>
                  </div>
                ))}
              </div>
              {pkg.bonus && (
                <div className="pt-3 border-t border-background-50/10">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-gift-line text-xs text-accent-400" />
                    </div>
                    <span className="text-xs text-accent-300 font-medium">{pkg.bonus}</span>
                  </div>
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-background-50/10 text-center">
                <span className="text-[11px] text-primary-300 font-medium inline-flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                  Tap to book this package
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="reveal-on-scroll flex flex-wrap justify-center gap-3 mb-10">
          {[
            { icon: "ri-drop-line", text: "All Drinks Iced" },
            { icon: "ri-cup-line", text: "9 oz Serving" },
            { icon: "ri-shopping-bag-line", text: "Min. 100 Cups" },
            { icon: "ri-gift-line", text: "20 Free Surplus Cups" },
            { icon: "ri-truck-line", text: "Transport Excluded" },
          ].map((tag) => (
            <span
              key={tag.text}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-background-200 bg-background-50/10 border border-background-50/10 rounded-full"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tag.icon} text-primary-400`} />
              </div>
              {tag.text}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 reveal-on-scroll">
          {eventTypes.map((type) => (
            <div
              key={type.label}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50/10 hover:bg-background-50/20 transition-all duration-300 hover:-translate-y-1 backdrop-blur-sm border border-background-50/10 cursor-default"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/20">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${type.icon} text-xl text-primary-300`} />
                </div>
              </div>
              <span className="text-xs font-medium text-background-200">{type.label}</span>
            </div>
          ))}
        </div>

        <div className="reveal-on-scroll bg-background-50/10 backdrop-blur-sm rounded-2xl p-6 border border-background-50/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-primary-300" />
                </div>
              </div>
              <div>
                <p className="text-xs text-background-300 mb-0.5">Call or WhatsApp</p>
                <p className="text-sm font-semibold text-background-50">013-3316383</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-primary-300" />
                </div>
              </div>
              <div>
                <p className="text-xs text-background-300 mb-0.5">Email</p>
                <a href="mailto:bhbcoffee26@gmail.com" className="text-sm font-semibold text-background-50 hover:text-primary-300 transition-colors">
                  bhbcoffee26@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-primary-300" />
                </div>
              </div>
              <div>
                <p className="text-xs text-background-300 mb-0.5">Based in</p>
                <p className="text-sm font-semibold text-background-50">Shah Alam, Selangor</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-background-50/10 text-center">
            <a
              href="#contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 hover:scale-105 active:scale-95 animate-pulse-glow cursor-pointer"
            >
              Get a Custom Quote
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Event Type Selection Modal */}
      {modalOpen && selectedPkg && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="bg-background-50 rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-background-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 id="modal-title" className="font-heading font-bold text-foreground-900 text-lg">
                  Book {selectedPkg.name}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">Select your event type to continue</p>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 transition-all cursor-pointer"
                aria-label="Close modal"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="mb-4 p-3 rounded-xl bg-primary-50 border border-primary-100">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-primary-600">RM{selectedPkg.price}</span>
                <span className="text-xs text-primary-500">per cup</span>
              </div>
              <p className="text-xs text-primary-600">
                Includes: {selectedPkg.drinks.join(", ")}
              </p>
              {selectedPkg.bonus && (
                <p className="text-xs text-accent-600 font-medium mt-1">
                  Bonus: {selectedPkg.bonus}
                </p>
              )}
            </div>

            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Choose Event Type</p>
            <div className="grid grid-cols-1 gap-2">
              {eventTypes.map((type) => (
                <button
                  key={type.label}
                  onClick={() => bookViaWhatsApp(type.label)}
                  className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer text-left"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${type.icon} text-lg text-primary-600`} />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-foreground-800">{type.label}</span>
                  <div className="ml-auto w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-foreground-400" />
                  </div>
                </button>
              ))}
            </div>

            <p className="text-[11px] text-foreground-400 text-center mt-4">
              You will be redirected to WhatsApp to complete your booking.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}